import { z } from 'zod';

// Time-of-use pricing: peak, off-peak, super off-peak and so on.
//
// A schedule has 1 to 4 periods. The first one covers "all other times" and has no hours of
// its own; each of the others has the hours it applies (days of the week, from and to), which
// can't overlap. Times are on 15-minute boundaries in the site's time zone. Every Australian
// time zone is a whole number of quarter hours from UTC, so a 15-minute step in UTC is a
// 15-minute step on the site's clock too, and sessions can be split on that grid.

export const SLOT_MINUTES = 15;
const SLOTS_PER_DAY = (24 * 60) / SLOT_MINUTES;
const SLOTS_PER_WEEK = SLOTS_PER_DAY * 7;
const SLOT_MS = SLOT_MINUTES * 60_000;
export const MAX_PERIODS = 4;

/** 1 = Monday … 7 = Sunday. */
export const WEEKDAYS = [1, 2, 3, 4, 5] as const;
export const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export interface TouWindow {
  /** 1 = Monday … 7 = Sunday. */
  days: number[];
  /** "HH:MM". */
  from: string;
  /** "HH:MM", "24:00" for midnight. Earlier than `from` means it runs past midnight. */
  to: string;
}
export interface TouPeriod {
  name: string;
  /** Empty for the first period ("all other times"). */
  windows: TouWindow[];
}
/** A period with its prices, as kept on a tariff. */
export interface PricedPeriod extends TouPeriod {
  /** $ per kWh. */
  pricePerKwh: number;
  /** Infinity EV's share, $ per kWh. */
  infinityevRatePerKwh: number | null;
}

/** One period, "All day", for chargers whose site has no schedule. */
export const ALL_DAY: TouPeriod[] = [{ name: 'All day', windows: [] }];

const time = z.string().regex(/^([01]\d|2[0-4]):(00|15|30|45)$/, 'Use a time on the quarter hour, e.g. 09:00').refine((t) => t <= '24:00', 'Use a time on the quarter hour, e.g. 09:00');
const minutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

const windowSchema = z
  .object({
    days: z.array(z.number().int().min(1).max(7)).min(1, 'Choose at least one day').max(7),
    from: time,
    to: time,
  })
  .refine((w) => w.from !== '24:00', { message: 'Start at 23:45 at the latest', path: ['from'] })
  .refine((w) => w.to === '24:00' || minutes(w.from) !== minutes(w.to), { message: 'The end time must differ from the start', path: ['to'] });

const periodSchema = z.object({
  name: z.string().trim().min(1, 'Name the period, e.g. Peak').max(32, 'At most 32 characters'),
  windows: z.array(windowSchema).max(14),
});

/** The 15-minute slots of the week (Monday 00:00 first) each window covers. */
function windowSlots(w: TouWindow): number[] {
  const from = minutes(w.from) / SLOT_MINUTES;
  const to = minutes(w.to) / SLOT_MINUTES;
  const out: number[] = [];
  for (const day of w.days) {
    const start = (day - 1) * SLOTS_PER_DAY;
    // Past midnight: runs on into the next day (Sunday into Monday).
    const length = to > from ? to - from : SLOTS_PER_DAY - from + to;
    for (let i = 0; i < length; i++) out.push((start + from + i) % SLOTS_PER_WEEK);
  }
  return out;
}

/** Which period each 15-minute slot of the week is in; overlaps are listed. */
export function weekSlots(periods: TouPeriod[]): { slots: number[]; overlaps: Array<[number, number]> } {
  const slots = new Array<number>(SLOTS_PER_WEEK).fill(0);
  const owner = new Array<number>(SLOTS_PER_WEEK).fill(-1);
  const overlaps: Array<[number, number]> = [];
  periods.forEach((p, i) => {
    if (i === 0) return;
    for (const w of p.windows) {
      for (const s of windowSlots(w)) {
        if (owner[s]! > 0 && owner[s] !== i) {
          if (!overlaps.some(([a, b]) => a === owner[s] && b === i)) overlaps.push([owner[s]!, i]);
          continue;
        }
        owner[s] = i;
        slots[s] = i;
      }
    }
  });
  return { slots, overlaps };
}

/** A schedule's periods: 1 to 4, the first with no hours, the others with hours that don't overlap. */
export const touPeriods = z
  .array(periodSchema)
  .min(1, 'A schedule needs at least one period')
  .max(MAX_PERIODS, `At most ${MAX_PERIODS} periods`)
  .superRefine((ps, ctx) => {
    if (ps[0] && ps[0].windows.length) ctx.addIssue({ code: 'custom', path: [0, 'windows'], message: 'The first period covers all other times, so it has no hours of its own' });
    ps.forEach((p, i) => {
      if (i > 0 && !p.windows.length) ctx.addIssue({ code: 'custom', path: [i, 'windows'], message: 'Add the hours this period applies' });
    });
    const names = ps.map((p) => p.name.trim().toLowerCase());
    names.forEach((n, i) => {
      if (names.indexOf(n) !== i) ctx.addIssue({ code: 'custom', path: [i, 'name'], message: 'Each period needs its own name' });
    });
    const { slots, overlaps } = weekSlots(ps);
    for (const [a, b] of overlaps) ctx.addIssue({ code: 'custom', path: [b, 'windows'], message: `Overlaps ${ps[a]!.name}` });
    if (ps.length > 1 && !slots.some((s) => s === 0)) ctx.addIssue({ code: 'custom', path: [0, 'name'], message: `${ps[0]!.name} has no hours left: the other periods cover the whole week` });
  });

export const rateScheduleBody = z.object({
  name: z.string().trim().min(1, 'Name the schedule, e.g. Business hours').max(64, 'At most 64 characters'),
  periods: touPeriods,
});
export type RateScheduleBody = z.input<typeof rateScheduleBody>;

// ---------------------------------------------------------------- clock

const formatters = new Map<string, Intl.DateTimeFormat>();
const DAY_INDEX: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/** Day of the week (0 = Monday) and minutes past midnight, on the clock of a time zone. */
export function localClock(at: Date, timezone: string): { day: number; minutes: number; date: string } {
  let f = formatters.get(timezone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-AU', { timeZone: timezone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    formatters.set(timezone, f);
  }
  const parts = Object.fromEntries(f.formatToParts(at).map((p) => [p.type, p.value]));
  return { day: DAY_INDEX[parts.weekday!] ?? 0, minutes: Number(parts.hour) * 60 + Number(parts.minute), date: `${parts.year}-${parts.month}-${parts.day}` };
}

function slotAt(at: Date, timezone: string): number {
  const c = localClock(at, timezone);
  return c.day * SLOTS_PER_DAY + Math.floor(c.minutes / SLOT_MINUTES);
}

/** The period in force at a moment. */
export function periodAt(periods: TouPeriod[], at: Date, timezone: string): number {
  if (periods.length < 2) return 0;
  return weekSlots(periods).slots[slotAt(at, timezone)] ?? 0;
}

/**
 * Splits energy delivered between two moments across the periods, in proportion to time
 * (the charger reports energy every few minutes, so each slice is short). Returns Wh per period.
 */
export function splitEnergy(periods: TouPeriod[], from: Date, to: Date, energyWh: number, timezone: string): number[] {
  const out = new Array<number>(periods.length).fill(0);
  if (energyWh <= 0) return out;
  const start = from.getTime();
  const end = to.getTime();
  if (periods.length < 2 || end <= start) {
    out[periodAt(periods, to, timezone)]! += energyWh;
    return out;
  }
  const { slots } = weekSlots(periods);
  const total = end - start;
  let t = start;
  while (t < end) {
    const next = Math.min(end, (Math.floor(t / SLOT_MS) + 1) * SLOT_MS);
    const p = slots[slotAt(new Date(t), timezone)] ?? 0;
    out[p]! += (energyWh * (next - t)) / total;
    t = next;
  }
  return out;
}

/** Cost in whole cents of the energy in each period at its price. */
export function periodCostCents(prices: number[], energyWh: number[]): number {
  return Math.round(energyWh.reduce((sum, wh, i) => sum + (wh / 1000) * (prices[i] ?? 0), 0) * 100);
}

/** When the period next changes, within a week: the time and the period it changes to. */
export function nextChange(periods: TouPeriod[], at: Date, timezone: string): { at: Date; period: number } | null {
  if (periods.length < 2) return null;
  const { slots } = weekSlots(periods);
  const now = slots[slotAt(at, timezone)] ?? 0;
  let t = (Math.floor(at.getTime() / SLOT_MS) + 1) * SLOT_MS;
  for (let i = 0; i < SLOTS_PER_WEEK; i++, t += SLOT_MS) {
    const p = slots[slotAt(new Date(t), timezone)] ?? 0;
    if (p !== now) return { at: new Date(t), period: p };
  }
  return null;
}

/** Today's periods on the site's clock, as "HH:MM" ranges, e.g. 00:00–07:00 Super off-peak. */
export function dayPlan(periods: TouPeriod[], at: Date, timezone: string): Array<{ from: string; to: string; period: number }> {
  if (periods.length < 2) return [{ from: '00:00', to: '24:00', period: 0 }];
  const { slots } = weekSlots(periods);
  const day = localClock(at, timezone).day;
  const hhmm = (slot: number) => {
    const m = slot * SLOT_MINUTES;
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  };
  const out: Array<{ from: string; to: string; period: number }> = [];
  for (let s = 0; s < SLOTS_PER_DAY; s++) {
    const p = slots[day * SLOTS_PER_DAY + s] ?? 0;
    const last = out[out.length - 1];
    if (last && last.period === p) last.to = hhmm(s + 1);
    else out.push({ from: hhmm(s), to: hhmm(s + 1), period: p });
  }
  return out;
}

/**
 * The most a time-limited session can cost: the charger at full power for every 15 minutes
 * of it, each at that quarter hour's price. In cents.
 */
export function maxCostForTime(periods: TouPeriod[], prices: number[], start: Date, minutesLong: number, kw: number, timezone: string): number {
  const end = start.getTime() + minutesLong * 60_000;
  const { slots } = weekSlots(periods);
  let t = start.getTime();
  let dollars = 0;
  while (t < end) {
    const next = Math.min(end, (Math.floor(t / SLOT_MS) + 1) * SLOT_MS);
    const p = periods.length < 2 ? 0 : (slots[slotAt(new Date(t), timezone)] ?? 0);
    dollars += kw * ((next - t) / 3_600_000) * (prices[p] ?? 0);
    t = next;
  }
  return Math.round(dollars * 100);
}

/** "Weekdays 09:00–18:00", "Sat, Sun 00:00–24:00". */
export function describeWindow(w: TouWindow): string {
  const days = [...w.days].sort((a, b) => a - b);
  const label =
    days.length === 7 ? 'Every day' : days.join() === '1,2,3,4,5' ? 'Weekdays' : days.join() === '6,7' ? 'Weekends' : days.map((d) => DAY_NAMES[d - 1]).join(', ');
  return `${label} ${w.from}–${w.to}`;
}
